import type { UseQueryResult } from 'react-query';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import type { ApiError } from '~/lib/api';
import type { IApiGetAppsResponseBody } from '../AppsApi.types';
import { IQueryKeyFactory } from '~/helpers/types';
import { useApi } from '~/lib/api/useApi';
import { getAppSettings } from '~/lib/appSettings';
import { appQueryKeyFactory } from './useAppQuery';

export type IAppsQueryKeyValues = {
  appRegion?: string;
};

export const appsQueryKeyFactory: IQueryKeyFactory<IAppsQueryKeyValues> = (
  values,
) => ['apps', values?.appRegion ?? null] as const;

export const useAppsQuery = (
  options?: UseQueryOptions<IApiGetAppsResponseBody, ApiError>,
): UseQueryResult<IApiGetAppsResponseBody, ApiError> => {
  const api = useApi();
  const queryClient = useQueryClient();
  const appRegion =
    api.getAuthorization()?.region ?? getAppSettings().defaultRegion;

  return useQuery({
    queryKey: appsQueryKeyFactory({ appRegion }),
    queryFn: () => api.apps.getAll(),
    onSuccess: (apps) => {
      // For the moment, apps are not paginated and not filtered, so we can just
      // set the data for app queries.
      apps.items.forEach((app) => {
        queryClient.setQueryData(
          appQueryKeyFactory({
            appId: app._id,
          })!,
          app,
        );
      });
      options?.onSuccess?.(apps);
    },
    ...options,
  });
};
